/**
 * ISO-3166-1 alpha-2 → ISO-4217 for user residency and browser locale regions when
 * resolving workspace budget currency from profile / navigator context.
 *
 * - {@link currencyForResidencyCountryIso2} returns `null` when the country is not listed,
 *   so callers can continue to browser locale, then USD.
 * - BRL is only associated with Brazil (`BR`).
 */

export const RESIDENCY_COUNTRY_TO_CURRENCY: Readonly<Record<string, string>> = Object.freeze({
  AO: "AOA",
  AR: "ARS",
  AT: "EUR",
  AU: "AUD",
  BE: "EUR",
  BR: "BRL",
  CA: "CAD",
  CH: "CHF",
  CL: "CLP",
  CN: "CNY",
  CO: "COP",
  DE: "EUR",
  ES: "EUR",
  ET: "ETB",
  FR: "EUR",
  GB: "GBP",
  GH: "GHS",
  IE: "EUR",
  IN: "INR",
  IT: "EUR",
  JP: "JPY",
  KE: "KES",
  MX: "MXN",
  MZ: "MZN",
  NG: "NGN",
  NL: "EUR",
  NZ: "NZD",
  PE: "PEN",
  PT: "EUR",
  SN: "XOF",
  TZ: "TZS",
  UK: "GBP",
  UG: "UGX",
  US: "USD",
  ZA: "ZAR",
  ZM: "ZMW",
  ZW: "USD",
})

/** @deprecated Use {@link RESIDENCY_COUNTRY_TO_CURRENCY}; kept for call sites that still import the old name. */
export const COUNTRY_CURRENCY_MAP: Readonly<Record<string, string>> = RESIDENCY_COUNTRY_TO_CURRENCY

/**
 * Looks up a currency for a known residency / region ISO-2 code.
 * Returns `null` if the code is invalid or not in {@link RESIDENCY_COUNTRY_TO_CURRENCY}
 * (caller should try browser locale, then USD).
 */
export function currencyForResidencyCountryIso2(iso2: string | null | undefined): string | null {
  const code = (iso2 ?? "").trim().toUpperCase()
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return null
  const cur = RESIDENCY_COUNTRY_TO_CURRENCY[code]
  return cur ? cur.trim().toUpperCase() : null
}

/** Map result or `USD` (e.g. UI default when picking a country in isolation). */
export function residencyDefaultCurrencyForCountry(iso2: string | null | undefined): string {
  return currencyForResidencyCountryIso2(iso2) ?? "USD"
}
